import { useLayoutEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  DOCS_SECTIONS_WITH_SUBSECTIONS,
  resolveDocsSubsectionId,
  type DocsSectionWithSubsections,
} from '../docsSubsections';

const SCROLL_TOP_OFFSET_PX = 120;
const MAX_SCROLL_ATTEMPTS = 24;
const RETRY_DELAY_MS = 50;

function isSubsectionSection(section: string | null): section is DocsSectionWithSubsections {
  return section !== null && (DOCS_SECTIONS_WITH_SUBSECTIONS as readonly string[]).includes(section);
}

export function getDocsSubsectionFromUrl(searchParams: URLSearchParams, hash: string): string | null {
  const fromQuery = searchParams.get('subsection');
  const fromHash = hash.replace(/^#/, '');
  const raw = fromQuery || fromHash;
  if (!raw) return null;
  return resolveDocsSubsectionId(raw);
}

export function useDocsSubsectionScroll(section: string | null, enabled: boolean) {
  const [searchParams] = useSearchParams();
  const { hash } = useLocation();
  const subsectionId = getDocsSubsectionFromUrl(searchParams, hash);

  useLayoutEffect(() => {
    if (!enabled || !subsectionId || !isSubsectionSection(section)) return;

    let cancelled = false;
    let attempts = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const scrollToSubsection = () => {
      if (cancelled) return;

      const el = document.getElementById(subsectionId);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_TOP_OFFSET_PX;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
      }

      if (attempts < MAX_SCROLL_ATTEMPTS) {
        attempts += 1;
        retryTimer = setTimeout(scrollToSubsection, RETRY_DELAY_MS);
      }
    };

    retryTimer = setTimeout(scrollToSubsection, 0);

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
    };
  }, [enabled, section, subsectionId]);

  return subsectionId;
}
